import React, { useEffect, useState } from 'react';
import { FaLock, FaExclamationTriangle, FaEnvelope, FaArrowRight } from 'react-icons/fa';

interface ExpiredScreenProps {
  message?: string;
}

/**
 * SubscriptionExpiredScreen — R-F02
 *
 * Fallback overlay when a 402 is surfaced before client routing lands on
 * `/renew-subscription` (axios also hard-redirects there).
 */
const SubscriptionExpiredScreen: React.FC<ExpiredScreenProps> = () => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('Your subscription has expired.');

  useEffect(() => {
    const handleExpired = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setMessage(detail?.message || 'Your subscription has expired. Please contact support to renew your access.');
      setVisible(true);
    };

    window.addEventListener('subscription-expired', handleExpired);
    return () => window.removeEventListener('subscription-expired', handleExpired);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Subscription Expired"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15, 23, 42, 0.92)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '48px 40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
        }}
      >
        {/* Lock icon */}
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: '#FEF2F2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}
        >
          <FaLock size={30} color="#EF4444" />
        </div>

        {/* Title */}
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
          Subscription Expired
        </h2>

        {/* Warning message */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            background: '#FFFBEB',
            border: '1px solid #FCD34D',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            textAlign: 'left',
          }}
        >
          <FaExclamationTriangle size={16} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
          <p style={{ color: '#92400E', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>{message}</p>
        </div>

        {/* Description */}
        <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', marginBottom: '32px' }}>
          Your access to trading strategies and premium features has been suspended.
          Please contact your account manager or support team to renew your subscription.
        </p>

        {/* CTA buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <a
            href="/renew-subscription"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: '#2563EB',
              color: '#ffffff',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: 600,
              textDecoration: 'none',
              transition: 'opacity 0.2s',
            }}
          >
            <FaArrowRight size={13} />
            Go to subscription renewal
          </a>

          <a
            href="mailto:support@platform.com?subject=Subscription%20Renewal%20Request"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'none',
              border: '1.5px solid #D1D5DB',
              borderRadius: '8px',
              padding: '11px 24px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#6B7280',
              textDecoration: 'none',
            }}
          >
            <FaEnvelope size={15} />
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionExpiredScreen;
